import React, { useEffect } from "react";
import MenuItem from "@mui/material/MenuItem";
import { Grid } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CustomTextField from "../../../../../components/CustomTextField";
import CustomCheckBox from "../../../../../components/CustomCheckBox";
import CustomRadioBox from "../../../../../components/CustomRadioBox";
import ReactHookFormSelect from "../../../../../components/ReactHookFormSelect";
import CustomSwitch from "../../../../../components/CustomSwitch";

const Common = ({ validationFields }) => {
  const {
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const { t } = useTranslation();

  const isAgrEnabled = watch("isAgrEnabled");
  const businessType = watch("businessType");
  const allowOnlineDeposit = watch("allowOnlineDeposit");

  useEffect(() => {
    if (!allowOnlineDeposit) {
      setValue("minimumDepositAmount", 0);
    }
  }, [allowOnlineDeposit]);

  return (
    <>
      <Grid container spacing={4} pt={3} px={2}>
        {/* ----------------------------------------------------------
          🔹 SECTION 1: NOTIFICATION SETTINGS
      ----------------------------------------------------------- */}
        <Grid item xs={12}>
          <h3 style={{ margin: 0, paddingBottom: 8 }}>Notification Settings</h3>
        </Grid>

        <Grid item md={2}>
          <CustomCheckBox
            label={t("email_notification")}
            name="isEmailEnabled"
            errors={errors}
            fields={validationFields}
          />
        </Grid>

        <Grid item md={2}>
          <CustomCheckBox
            label="SMS Notification"
            name="isSmsEnabled"
            errors={errors}
            fields={validationFields}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={4}
        pt={3}
        px={2}
        sx={{
          "& > .MuiGrid-item": {
            marginBottom: 3, // Adds margin to every grid item
          },
        }}
      >
        {/* ----------------------------------------------------------
          🔹 SECTION 2: INVOICE SETTINGS
      ----------------------------------------------------------- */}
        <Grid item xs={12}>
          <h3 style={{ margin: 0, paddingBottom: 8 }}>Invoice Settings</h3>
        </Grid>

        <Grid item md={4}>
          <CustomTextField
            label={t("powered_by")}
            name="poweredBy"
            control={control}
            errors={errors}
            fields={validationFields}
          />
        </Grid>

        <Grid item md={4}>
          <CustomTextField
            label={t("logo_text")}
            name="logoText"
            control={control}
            errors={errors}
            fields={validationFields}
          />
        </Grid>

        <Grid item md={4}>
          <CustomCheckBox
            label="Invoice Prefix"
            name="invoicePrefix"
            errors={errors}
            fields={validationFields}
          />
        </Grid>
        <Grid item md={4}>
          <CustomTextField
            label={t("caf_prefix")}
            name="cafPrefix"
            control={control}
            errors={errors}
            fields={validationFields}
          />
        </Grid>

        <Grid item md={4}>
          <ReactHookFormSelect
            id="invoiceTemplate"
            name="invoiceTemplate"
            label={t("invoice_template")}
            control={control}
            fullWidth
            errors={errors}
            fields={validationFields}
          >
            <MenuItem value={"InvoiceDefault"}>Default</MenuItem>
            <MenuItem value={"InvoiceTemplate1"}>Template 1</MenuItem>
          </ReactHookFormSelect>
        </Grid>
        <Grid item md={4}>
          <ReactHookFormSelect
            id="defaultInvoiceStatus"
            name="defaultInvoiceStatus"
            label={t("default_invoice_status")}
            control={control}
            fullWidth
            errors={errors}
            fields={validationFields}
          >
            <MenuItem value={"PAID"}>Paid</MenuItem>
            <MenuItem value={"UN_PAID"}>Unpaid</MenuItem>
          </ReactHookFormSelect>
        </Grid>
      </Grid>
      <Grid container spacing={4} pt={3} px={2}>
        {/* ----------------------------------------------------------
          🔹 SECTION 3: USER PREFERENCES
      ----------------------------------------------------------- */}
        <Grid item xs={12}>
          <h3 style={{ margin: 0, paddingBottom: 8 }}>User Preferences</h3>
        </Grid>

        <Grid item md={4}>
          <CustomTextField
            label={t("Default Expiry Time")}
            name="defaultExpiryTime"
            control={control}
            errors={errors}
            fields={validationFields}
          />
        </Grid>

        <Grid item md={4}>
          <CustomRadioBox
            label="Expiry Mode"
            name="defaultExpiry"
            control={control}
            options={[
              { value: "false", label: "Default Expiry" },
              { value: "true", label: "Month End Expiry" },
            ]}
            errors={errors}
          />
        </Grid>

        <Grid item md={4}>
          <CustomCheckBox
            label="Show Parental Guidance"
            name="isParentalEnabled"
            errors={errors}
            fields={validationFields}
          />
        </Grid>

        <Grid item md={4}>
          <CustomCheckBox
            label={t("allowOnlineDeposit")}
            name="allowOnlineDeposit"
            errors={errors}
            fields={validationFields}
          />
        </Grid>
        {allowOnlineDeposit && (
          <Grid item md={4}>
            <CustomTextField
              label={t("minimum_deposit_amount")}
              name="minimumDepositAmount"
              control={control}
              errors={errors}
              fields={validationFields}
            />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={4} pt={3} px={2}>
        {/* ----------------------------------------------------------
          🔹 SECTION 4: TAX & LEGAL DETAILS (ONLY FOR MSO)
      ----------------------------------------------------------- */}
        {businessType === "MULTI_SERVICE_OPERATOR" && (
          <>
            <Grid item xs={12}>
              <h3 style={{ margin: 0, paddingBottom: 8 }}>Tax & Legal Details</h3>
            </Grid>

            <Grid item md={4}>
              <CustomTextField
                label={t("pan")}
                name="pan"
                control={control}
                errors={errors}
                fields={validationFields}
              />
            </Grid>

            <Grid item md={4}>
              <CustomTextField
                label={t("hsn_code")}
                name="hsnCode"
                control={control}
                errors={errors}
                fields={validationFields}
              />
            </Grid>

            <Grid item md={3}>
              <CustomSwitch
                label={t("is_multiple_gst")}
                name="isMultipleGst"
                errors={errors}
                fields={validationFields}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container spacing={4} pt={3} px={2}>
        {/* ----------------------------------------------------------
          🔹 SECTION 5: AGR SETTINGS
      ----------------------------------------------------------- */}
        <Grid item xs={12}>
          <h3 style={{ margin: 0, paddingBottom: 8 }}>AGR Settings</h3>
        </Grid>

        <Grid item md={3}>
          <CustomSwitch
            label={t("is_agr_enabled")}
            name="isAgrEnabled"
            errors={errors}
            fields={validationFields}
          />
        </Grid>

        {isAgrEnabled && (
          <Grid item md={4}>
            <CustomTextField
              label={t("agr")}
              name="agrSharing"
              control={control}
              disabled
              errors={errors}
              fields={validationFields}
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={4} pt={3} px={2}>
        {/* ----------------------------------------------------------
          🔹 SECTION 6: RENEWAL SETTINGS
      ----------------------------------------------------------- */}
        <Grid item xs={12}>
          <h3 style={{ margin: 0, paddingBottom: 8 }}>Renewal Settings</h3>
        </Grid>

        <Grid item md={3}>
          <CustomSwitch
            label={t("is_immediate_renewal")}
            name="isImmediateRenewal"
            errors={errors}
            fields={validationFields}
          />
        </Grid>
        <Grid item md={4}>
          <CustomTextField
            label={t("number_of_hold_limit")}
            name="numberOfHoldLimit"
            control={control}
            errors={errors}
            fields={validationFields}
          />
        </Grid>
        {businessType === "MULTI_SERVICE_OPERATOR" && (
          <Grid item md={3}>
            <CustomSwitch
              label="Restricted for Unpaid Invoice"
              name="isRestrictRenewal"
              errors={errors}
              fields={validationFields}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default Common;
